/* Float the original renderer: SVG references and live design updates stay intact. */
(() => {
  if (!document.documentElement.classList.contains('np-pod-product')) return;
  if (document.documentElement.dataset.npMobilePreviewBound) return;
  document.documentElement.dataset.npMobilePreviewBound = 'true';
  const mobile = matchMedia('(max-width:749px)');
  let source, native, form, section, panel, backdrop, reopen, panSurface, resizeObserver, observer, observedLoaded;
  let frame = 0, engaged = false, dismissed = false, expanded = false, active = false, lastEditable = null;
  let naturalWidth = 0, naturalHeight = 0, placeholderHeight = 0, layoutWidth = 0;
  let zoom = 1, panX = 0, panY = 0, fitScale = 1, drag = null;
  let viewportWidth = innerWidth, restingHeight = window.visualViewport?.height || innerHeight;
  const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
  const editable = element => element instanceof Element && element.matches('input:not([type=radio]):not([type=checkbox]):not([type=hidden]),textarea,[contenteditable=true]');
  const setActive = value => {
    if (active === value) return;
    active = value;
    source?.classList.toggle('np-mobile-preview-source', value);
    panel.hidden = !value;
    backdrop.hidden = !value;
  };
  const applyZoom = () => {
    const limitX = naturalWidth * (zoom - 1) / 2;
    const limitY = naturalHeight * (zoom - 1) / 2;
    panX = Math.max(-limitX, Math.min(limitX, panX));
    panY = Math.max(-limitY, Math.min(limitY, panY));
    source?.style.setProperty('--np-preview-zoom', String(zoom));
    source?.style.setProperty('--np-preview-pan-x', `${panX}px`);
    source?.style.setProperty('--np-preview-pan-y', `${panY}px`);
    panel.querySelector('[data-preview-zoom-in]').disabled = zoom >= 3;
    panel.querySelector('[data-preview-zoom-out]').disabled = zoom <= 1;
    panel.querySelector('[data-preview-zoom-reset]').textContent = `${zoom}×`;
    panel.dataset.zoom = String(zoom);
    panel.querySelector('.np-mobile-preview__hint').hidden = zoom <= 1;
    panSurface.hidden = zoom <= 1;
  };
  const setZoom = value => { zoom = Math.max(1, Math.min(3, value)); applyZoom(); };
  function build() {
    backdrop = document.createElement('div');
    backdrop.className = 'np-mobile-preview-backdrop';
    backdrop.hidden = true;
    backdrop.setAttribute('aria-hidden', 'true');
    panel = document.createElement('aside');
    panel.className = 'np-mobile-preview';
    panel.hidden = true;
    panel.setAttribute('aria-label', 'Live personalized design preview');
    const magnifier = plus => `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10" cy="10" r="6.5"/><path d="m15 15 5.5 5.5M7 10h6${plus ? 'M10 7v6' : ''}"/></svg>`;
    panel.innerHTML = `<div class="np-mobile-preview__bar"><span>Live preview</span><div class="np-mobile-preview__actions"><button type="button" data-preview-expand aria-label="Enlarge preview" aria-expanded="false">Enlarge</button><button type="button" class="np-mobile-preview__close" aria-label="Close live preview">×</button></div></div><div class="np-mobile-preview__pan" hidden aria-hidden="true"></div><div class="np-mobile-preview__zoom" role="group" aria-label="Design zoom"><button type="button" data-preview-zoom-in aria-label="Zoom in design">${magnifier(true)}</button><button type="button" data-preview-zoom-reset aria-label="Reset design zoom">1×</button><button type="button" data-preview-zoom-out aria-label="Zoom out design" disabled>${magnifier(false)}</button></div><span class="np-mobile-preview__hint" hidden>Drag to move</span>`;
    panSurface = panel.querySelector('.np-mobile-preview__pan');
    reopen = document.createElement('button');
    reopen.type = 'button';
    reopen.className = 'np-mobile-preview-reopen';
    reopen.textContent = 'Show preview';
    reopen.hidden = true;
    document.body.append(backdrop, panel, reopen);
    panel.querySelector('[data-preview-zoom-in]').addEventListener('click', () => setZoom(zoom + .5));
    panel.querySelector('[data-preview-zoom-out]').addEventListener('click', () => setZoom(zoom - .5));
    panel.querySelector('[data-preview-zoom-reset]').addEventListener('click', () => setZoom(1));
    panSurface.addEventListener('pointerdown', event => {
      if (event.button !== 0 || zoom <= 1) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, panX, panY };
      panSurface.setPointerCapture(event.pointerId);
    });
    panSurface.addEventListener('pointermove', event => {
      if (!drag || drag.id !== event.pointerId) return;
      panX = drag.panX + (event.clientX - drag.x) / fitScale;
      panY = drag.panY + (event.clientY - drag.y) / fitScale;
      applyZoom();
    });
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type => panSurface.addEventListener(type, () => { drag = null; }));
    panel.querySelector('[data-preview-expand]').addEventListener('click', event => {
      expanded = !expanded;
      event.currentTarget.textContent = expanded ? 'Reduce' : 'Enlarge';
      event.currentTarget.setAttribute('aria-label', expanded ? 'Reduce preview' : 'Enlarge preview');
      event.currentTarget.setAttribute('aria-expanded', String(expanded));
      // Keep the last edited field below the larger panel before range checks run.
      if (expanded && lastEditable?.isConnected && form?.contains(lastEditable)) {
        const fieldRect = lastEditable.getBoundingClientRect();
        const viewportHeight = window.visualViewport?.height || innerHeight;
        const safeTop = Math.min(400, Math.floor(viewportHeight * .6)) + 16;
        if (fieldRect.bottom > 44 && fieldRect.top < safeTop) {
          window.scrollBy({ top: fieldRect.top - safeTop, behavior: 'instant' });
        }
      }
      schedule();
    });
    panel.querySelector('.np-mobile-preview__close').addEventListener('click', () => {
      dismissed = true;
      setZoom(1);
      setActive(false);
      schedule();
      reopen.focus({ preventScroll: true });
    });
    reopen.addEventListener('click', () => { dismissed = false; schedule(); });
  }
  function bind() {
    const next = document.querySelector('#MainContent [data-np-pod-gallery]');
    const renderer = next?.querySelector('#tee-gallery');
    const nextForm = document.querySelector('#MainContent #tee-artwork-form');
    if (!next || !renderer || !nextForm) return false;
    if (source === next && native === renderer && form === nextForm) return true;
    if (panel) setActive(false); else build();
    resizeObserver?.disconnect();
    observer?.disconnect();
    source = next; native = renderer; form = nextForm; lastEditable = null;
    section = source.closest('[id^="MainProduct-"]') || source.closest('.shopify-section');
    naturalWidth = 0; layoutWidth = 0; engaged = false; dismissed = false;
    observedLoaded = null;
    resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(source);
    resizeObserver.observe(form);
    observer = new MutationObserver(schedule);
    observer.observe(source, { attributes: true, attributeFilter: ['data-np-pod-gallery'], childList: true });
    return true;
  }
  function update() {
    frame = 0;
    if (!bind()) return;
    const visual = window.visualViewport;
    const viewportHeight = visual?.height || innerHeight;
    if (Math.abs(innerWidth - viewportWidth) > 20) {
      viewportWidth = innerWidth;
      restingHeight = viewportHeight;
    }
    restingHeight = Math.max(restingHeight, viewportHeight);
    const keyboard = editable(document.activeElement) && viewportHeight < restingHeight - 120;
    const modal = [...document.querySelectorAll('dialog[open],.drawer.active,details.menu-drawer-container[open],.vm--container,[role="dialog"][aria-modal="true"]')].some(el => el.getBoundingClientRect().height > 0 && getComputedStyle(el).visibility !== 'hidden');
    const loaded = native.querySelector('.tee-mockup-item--loaded');
    if (loaded && observedLoaded !== loaded) {
      if (observedLoaded) resizeObserver.unobserve(observedLoaded);
      resizeObserver.observe(loaded);
      observedLoaded = loaded;
    }
    const ready = engaged && source.dataset.npPodGallery === 'live' && loaded && (!form.closest('np-pod-slot') || form.closest('np-pod-slot').dataset.state === 'ready');
    if (!mobile.matches || !ready || keyboard || modal || viewportHeight < 430) {
      setActive(false); reopen.hidden = true; return;
    }
    if (!active) {
      layoutWidth = source.getBoundingClientRect().width;
      const nativeRect = native.getBoundingClientRect();
      const renderedRect = loaded.getBoundingClientRect();
      // The app can retain the previous graphic size after its container shrinks.
      naturalWidth = renderedRect.width || nativeRect.width;
      naturalHeight = renderedRect.height || native.querySelector('.tee-gallery__stage')?.getBoundingClientRect().height || nativeRect.height;
      placeholderHeight = nativeRect.height;
    }
    // Re-measure in normal flow if either the layout or app-rendered graphic changes.
    if (active) {
      const renderedRect = loaded.getBoundingClientRect();
      const renderedScale = fitScale * zoom;
      const graphicChanged = renderedRect.width > 0 && renderedRect.height > 0 && (Math.abs(renderedRect.width / renderedScale - naturalWidth) > 2 || Math.abs(renderedRect.height / renderedScale - naturalHeight) > 2);
      if (Math.abs(source.getBoundingClientRect().width - layoutWidth) > 2 || graphicChanged) {
        setActive(false); schedule(); return;
      }
    }
    const previewHeight = Math.min(expanded ? 400 : 260, Math.floor(viewportHeight * (expanded ? .6 : .34)));
    // Keep zoom controls below the fitted image, including at 1x.
    const toolbarHeight = 58; // 56px controls plus the panel border and a gap.
    const imageHeight = previewHeight - 44 - toolbarHeight;
    const rect = source.getBoundingClientRect();
    const formRect = form.getBoundingClientRect();
    const purchase = section?.querySelector('.main-product-atc,.tee-btn--atc,button[name="add"]');
    const purchaseTop = purchase?.getBoundingClientRect().top ?? formRect.bottom;
    // Only cover the top of the screen while the buyer is within personalization.
    const inRange = rect.top + placeholderHeight < 44 && formRect.bottom > previewHeight + 24 && purchaseTop > previewHeight + 24;
    if (!inRange || !naturalWidth || !naturalHeight) {
      setActive(false); reopen.hidden = true; return;
    }
    reopen.hidden = !dismissed;
    if (dismissed) { setActive(false); return; }
    const scale = Math.min(imageHeight / naturalHeight, (document.documentElement.clientWidth - 24) / naturalWidth, 1);
    const imageTop = 44 + (imageHeight - naturalHeight * scale) / 2;
    source.style.setProperty('--np-preview-placeholder', `${placeholderHeight}px`);
    source.style.setProperty('--np-preview-width', `${naturalWidth}px`);
    source.style.setProperty('--np-preview-natural-height', `${naturalHeight}px`);
    source.style.setProperty('--np-preview-scale', String(scale));
    source.style.setProperty('--np-preview-top', `${imageTop}px`);
    panel.style.setProperty('--np-preview-height', `${previewHeight}px`);
    backdrop.style.setProperty('--np-preview-height', `${previewHeight}px`);
    fitScale = scale;
    panSurface.style.width = `${naturalWidth * scale}px`;
    panSurface.style.height = `${naturalHeight * scale}px`;
    panSurface.style.top = `${imageTop}px`;
    applyZoom();
    setActive(true);
  }
  const onPersonalize = event => {
    if (!event.isTrusted || !(event.target instanceof Element)) return;
    if (!event.target.closest('#tee-artwork-form,variant-selects,[data-np-pod-picker]')) return;
    if (!bind()) return;
    engaged = true;
    if (editable(event.target)) lastEditable = event.target;
    schedule();
  };
  ['input','change','click'].forEach(type => document.addEventListener(type, onPersonalize, { capture: true }));
  document.addEventListener('scroll', schedule, { capture: true, passive: true });
  document.addEventListener('focusin', () => {
    schedule();
    requestAnimationFrame(() => {
      const target = document.activeElement;
      if (!active || !editable(target)) return;
      const top = target.getBoundingClientRect().top;
      const safeTop = panel.getBoundingClientRect().bottom + 16;
      if (top < safeTop) target.scrollIntoView({ block: 'center', behavior: 'instant' });
    });
  });
  document.addEventListener('focusout', schedule);
  document.addEventListener('click', schedule);
  new MutationObserver(schedule).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  window.addEventListener('resize', schedule, { passive: true });
  window.visualViewport?.addEventListener('resize', schedule, { passive: true });
  window.visualViewport?.addEventListener('scroll', schedule, { passive: true });
  mobile.addEventListener('change', schedule);
  document.addEventListener('shopify:section:load', schedule);
  // The app creates both form and renderer asynchronously. Disconnect after handover.
  const boot = new MutationObserver(() => { if (bind()) { boot.disconnect(); schedule(); } });
  boot.observe(document.body, { childList: true, subtree: true });
  if (bind()) { boot.disconnect(); schedule(); }
})();
